import React from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { useForcedNav } from "./hooks/use-forced-nav";
import {
    GlobalNavLinkContentProps,
    useGlobalNavContext
} from "./nav-links-container";

export type GlobalNavLinkProps = GlobalNavLinkContentProps & {
    isLinkVisible?: boolean;
};

const buildAnchorLinkClassName = (
    navLinkIsCurrentPage: boolean,
    isCurrentOpenDropdown: boolean,
    hasSubnav: boolean,
    isActive?: boolean
): string => {
    return (
        `glob-nav-anchor-link nav-link-anchor` +
        `${navLinkIsCurrentPage ? " isCurrentPage " : ""}` +
        `${!!isCurrentOpenDropdown && !!hasSubnav ? "isActive" : ""}` +
        `${!hasSubnav ? "isHoverableLink" : ""}` +
        `${isActive ? " isActive " : ""}`
    );
};

const GlobalNavLink = (props: GlobalNavLinkProps) => {
    // Using useGlobalNavContext hook which uses context, so component must memoize
    // to prevent unnecessary re-renders when context changes but props don't
    const { current, setCurrent, setCurrentByClick, isClickLocked } =
        useGlobalNavContext();
    const { isForcingNav, forcedNavKey } = useForcedNav();

    const isHover = !checkFeatureFlagGate(featureFlags.gwpGlobalNavHoverVClick);

    const isCurrentOpenDropdown = current === props.globalLinksKey;
    const normalizePathname = (path: string) => path.replace(/\/$/, "");
    const navLinkIsCurrentPage =
        props.href &&
        normalizePathname(globalThis.location.pathname) ===
            normalizePathname(props.href);

    const anchorLinkClassName = buildAnchorLinkClassName(
        !!navLinkIsCurrentPage,
        isCurrentOpenDropdown,
        !!props.hasSubnav,
        !!props.isActive
    );

    const linkClickAction: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
        if (props.hasSubnav) {
            e.preventDefault();

            if (isForcingNav && forcedNavKey !== props.globalLinksKey) {
                return;
            }

            // Use isCurrentOpenDropdown (from context) as the source of truth
            // rather than local isOpen state, which can drift out of sync when
            // the user hovers between items or the context is updated externally.
            if (isCurrentOpenDropdown && isClickLocked) {
                // Already click-locked open → second click closes it
                setCurrentByClick("");
            } else if (isCurrentOpenDropdown && !isClickLocked) {
                // Opened by hover, first click → keep it open and click-lock it
                setCurrentByClick(props.globalLinksKey);
            } else {
                // Not open → open it via click (click-locks it)
                setCurrentByClick(props.globalLinksKey);
            }
        }
    };

    const rotate180deg = css({
        transform: "rotate(180deg)"
    });

    return (
        <a
            className={anchorLinkClassName}
            style={{
                pointerEvents: props.isLinkVisible === false ? "none" : "auto"
            }}
            css={css({
                paddingBlockStart: token("space.100"),
                display: "flex",
                alignItems: "center",
                height: "100%",
                textDecoration: "none",
                lineHeight: "1rem",

                // if padding val needs to change, update calculateMaxChildren function (search for jfind-1024)

                paddingInlineStart: token("space.100"),
                paddingInlineEnd: token("space.300"),
                ["&.isActive"]: {
                    color: wacToken("color.extended.blue800")
                },
                ["&:focus-visible"]: {
                    outlineOffset: "-2px"
                },
                ["&:hover"]: {
                    color: wacToken("color.primary.blue")
                },
                [media.above.md]: {
                    borderRadius: "3px",
                    textDecoration: "none"
                }
            })}
            href={props.href ?? "#"}
            onClick={linkClickAction}
            onMouseEnter={
                isHover
                    ? () => {
                          if (
                              isForcingNav &&
                              forcedNavKey !== props.globalLinksKey
                          ) {
                              return;
                          }

                          // When a subnav is click-locked open, hovering other
                          // links should not switch the active subnav.
                          if (isClickLocked && !isCurrentOpenDropdown) {
                              return;
                          }

                          setCurrent(props.globalLinksKey);
                      }
                    : undefined
            }
            onFocus={
                () => {
                    return;
                } /* clicks are supported, using empty function for ratcheted @atlassian/a11y/mouse-events-have-key-events lint rule */
            }
            aria-label={props.linkText}
            aria-expanded={
                props.hasSubnav ? !!isCurrentOpenDropdown : undefined
            }
            aria-current={navLinkIsCurrentPage ? "page" : undefined}
            role="menuitem"
            aria-haspopup={props.hasSubnav ? "true" : undefined}
            data-event="clicked"
            data-event-component="linkButton"
            data-event-container="headerDropdownLink"
            data-product-key={props.productKey ?? "jiraSoftware"}
        >
            {props.linkText}
            {!!props.hasSubnav && (
                <div
                    className="chevron-icon"
                    style={{
                        opacity: props.isLinkVisible === false ? 0 : 1
                    }}
                    css={[
                        css({
                            display: "block",
                            marginInlineStart: "8px",
                            lineHeight: 0,
                            transition: "250ms"
                        }),
                        isCurrentOpenDropdown && rotate180deg
                    ]}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="11"
                        height="6"
                        viewBox="0 0 11 6"
                        fill="none"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <path
                            d="M9.19922 1.18176L5.19922 4.81813L1.19922 1.18176"
                            stroke={
                                isCurrentOpenDropdown
                                    ? wacToken("color.extended.blue800")
                                    : wacToken("color.primary.black")
                            }
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            )}
        </a>
    );
};

// Memoize to prevent unnecessary re-renders when parent component updates
// but props haven't changed. This is important because GlobalNavLink consumes
// GlobalNavContext which updates frequently (when dropdowns open/close),
// and we only want to re-render this link if its props actually change.
export default React.memo(GlobalNavLink);
